import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { FunctionComponent } from 'react';
import type { citations } from '@/features/client-onboarding/components/citations';

interface SlideConfig {
  slidePage: FunctionComponent<ClientIdProps> | null;
  nextButtonLabel: string;
  nextSlidePage?: FunctionComponent<ClientIdProps>;
  previousSlidePage?: FunctionComponent<ClientIdProps>;
  optionalCtaLabel?: string;
  optionalCtaPage?: FunctionComponent<ClientIdProps>;
  secondaryNextButtonLabel?: string;
  secondaryNextSlidePage?: FunctionComponent<ClientIdProps>;
  disableNextButton?: boolean;
  errorModalMessage?: string;
  shouldHideNextNavigation?: boolean;
  hideProgressStepper?: boolean;
  citations?: (typeof citations)['value'][];
  onNextClick?: () => void;
  onBackClick?: () => void;
}

interface OnboardingNavigationState {
  currentSlideIndex: number;
  previousSlideIndex: number;
  slideHistory: number[];
  slideConfig: SlideConfig;
  isMobileMenuOpen: boolean;
}

interface OnboardingNavigationActions {
  updateSlide: (globalSlideIndex: number) => void;
  setSlideConfig: (config: Partial<SlideConfig>) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  reset: () => void;
}

type OnboardingNavigationStore = OnboardingNavigationState & OnboardingNavigationActions;

const DEFAULT_SLIDE_CONFIG: SlideConfig = {
  slidePage: null,
  nextButtonLabel: 'Next',
};

const INITIAL_STATE: OnboardingNavigationState = {
  currentSlideIndex: 0,
  previousSlideIndex: 0,
  slideHistory: [],
  slideConfig: DEFAULT_SLIDE_CONFIG,
  isMobileMenuOpen: false,
};

export const useOnboardingNavigationStore = create<OnboardingNavigationStore>((set, get) => ({
  ...INITIAL_STATE,

  updateSlide: (globalSlideIndex: number) => {
    const state = get();

    if (state.currentSlideIndex === globalSlideIndex) {
      return;
    }

    let previousSlideIndex: number;

    if (globalSlideIndex < state.currentSlideIndex) {
      const lastVisit = state.slideHistory.findLast(item => item < globalSlideIndex);
      previousSlideIndex = lastVisit ?? 0;
    } else {
      previousSlideIndex = state.currentSlideIndex;
    }

    set({
      currentSlideIndex: globalSlideIndex,
      previousSlideIndex,
      slideHistory: [...state.slideHistory, globalSlideIndex],
    });
  },

  setSlideConfig: (config: Partial<SlideConfig>) => {
    set({
      slideConfig: { ...DEFAULT_SLIDE_CONFIG, ...config },
    });
  },

  toggleMobileMenu: () => {
    set(state => ({ isMobileMenuOpen: !state.isMobileMenuOpen }));
  },

  closeMobileMenu: () => {
    set({ isMobileMenuOpen: false });
  },

  reset: () => set(INITIAL_STATE),
}));

export const useSlideHistory = () => useOnboardingNavigationStore(state => state.slideHistory);

export const usePreviousSlideIndex = () =>
  useOnboardingNavigationStore(state => state.previousSlideIndex);

export const useCurrentSlideIndex = () =>
  useOnboardingNavigationStore(state => state.currentSlideIndex);

export const useSlideConfig = () => useOnboardingNavigationStore(state => state.slideConfig);

export const useIsMobileMenuOpen = () =>
  useOnboardingNavigationStore(state => state.isMobileMenuOpen);

export const useMobileMenuActions = () =>
  useOnboardingNavigationStore(
    useShallow(state => ({
      toggleMobileMenu: state.toggleMobileMenu,
      closeMobileMenu: state.closeMobileMenu,
    })),
  );

export const useOnboardingNavigationActions = () =>
  useOnboardingNavigationStore(
    useShallow(state => ({
      updateSlide: state.updateSlide,
      setSlideConfig: state.setSlideConfig,
      reset: state.reset,
    })),
  );
